import React from 'react'

import {
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TableSortLabel,
} from '@material-ui/core'

import { Address, ModelMap, WorkSpaceUser } from '../../../models'
import { HeadCell, RowData } from '../../../components'
import { UserActivity } from '../../../hooks/useOrganizationManagement'

type Order = 'asc' | 'desc'
type SortableColumns = 'name' | 'last-activity' | 'activity-count'

const TABLE_HEAD_CELLS: HeadCell<RowData>[] = [
    {
        id: 'name',
        label: 'Name',
        align: 'left',
        disablePadding: false,
        width: 200,
        sortable: true,
    },
    {
        id: 'address',
        label: 'Address',
        align: 'left',
        disablePadding: false,
        width: 250,
        sortable: false,
    },
    {
        id: 'permissions',
        label: 'Current Permission Roles',
        align: 'left',
        disablePadding: false,
        width: 200,
        sortable: false,
    },
    {
        id: 'last-activity',
        label: 'Last Activity',
        align: 'left',
        disablePadding: false,
        width: 200,
        sortable: true,
    },
    {
        id: 'activity-count',
        label: '30-Day Activity',
        align: 'left',
        disablePadding: false,
        width: 200,
        sortable: true,
    },
]

const useLocalStyles = makeStyles((theme) => ({
    tableWrapper: {
        flex: 1,
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 380px)',
    },
    stickyHeader: {
        backgroundColor: theme.palette.background.paper,
    },
    table: {
        minWidth: 750,
        tableLayout: 'fixed',
    },
    tableBody: {
        height: 'auto',
    },
    tableRow: {
        height: 48,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    emptySpace: {
        flex: 1,
        minHeight: 0,
    },
}))

interface Props {
    workspaceUsers: WorkSpaceUser[]
    userActivityMap: ModelMap<UserActivity>
    order: Order
    orderBy: SortableColumns
    onSort: (property: SortableColumns) => void
}

export const UserActivityTable = ({
    workspaceUsers,
    userActivityMap,
    order,
    orderBy,
    onSort,
}: Props) => {
    const localClasses = useLocalStyles()

    return (
        <TableContainer component={Paper} className={localClasses.tableWrapper}>
            <Table className={localClasses.table} size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {TABLE_HEAD_CELLS.map((cell) => (
                            <TableCell
                                key={cell.id}
                                align={
                                    cell.id === 'check' ? 'left' : cell.align
                                }
                                padding={
                                    cell.id === 'check'
                                        ? 'checkbox'
                                        : cell.disablePadding
                                        ? 'none'
                                        : 'default'
                                }
                                className={localClasses.stickyHeader}
                                style={{ width: cell.width }}
                            >
                                {cell.sortable ? (
                                    <TableSortLabel
                                        active={orderBy === cell.id}
                                        direction={
                                            orderBy === cell.id ? order : 'asc'
                                        }
                                        onClick={() =>
                                            onSort(cell.id as SortableColumns)
                                        }
                                    >
                                        {cell.label}
                                    </TableSortLabel>
                                ) : (
                                    cell.label
                                )}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody className={localClasses.tableBody}>
                    {workspaceUsers.map((workspaceUser) => {
                        const company = workspaceUser.active_workspace
                        const userActivity = userActivityMap[workspaceUser.id]
                        return (
                            <TableRow
                                key={`ROW_${company.id}`}
                                hover
                                className={localClasses.tableRow}
                            >
                                <TableCell>{company.name}</TableCell>
                                <TableCell>
                                    <AddressCell
                                        address={
                                            company.primary_address ?? null
                                        }
                                    />
                                </TableCell>
                                <TableCell>
                                    {workspaceUser.permission_roles?.length ? (
                                        <Typography variant="body2">
                                            {workspaceUser.permission_roles
                                                .map((role) => role.name)
                                                .join(', ')}
                                        </Typography>
                                    ) : (
                                        <div />
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {userActivity?.latest_timestamp
                                            ? new Date(
                                                  userActivity.latest_timestamp,
                                              ).toLocaleString()
                                            : 'N/A'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {userActivity?.log_count
                                            ? userActivity.log_count
                                            : 'N/A'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
            <div className={localClasses.emptySpace} />
        </TableContainer>
    )
}

const AddressCell = ({ address }: { address: Address | null }) => {
    if (!address) {
        return <Typography variant="subtitle2">N/A</Typography>
    }

    return (
        <div>
            <Typography variant="subtitle2">{address.address1}</Typography>
            <Typography variant="subtitle2">
                {address.city}, {address.state} {address.zip_code}
            </Typography>
        </div>
    )
}
